/**
 * PythonPackageManagerService - Service for managing Python package installations
 *
 * This service handles all Python package management operations including
 * installing packages, wheels, tracking installations, and managing dependencies.
 */

import { Injectable } from '@angular/core';
import { CdnModuleService } from '../../cdn-module/cdn-module.service';
import { PyodideFileJournal } from '../journal/python-journal.helper';
import {
  PackageInstallConfig,
  PackageInstallResult,
  WheelPackageInfo,
  PackageVerificationResult,
  PackageInstallationTracker
} from '../interfaces/python-package.interface';
import { PythonFileSystemHelper } from '../filesystem/python-filesystem.helper';

@Injectable({
  providedIn: 'root'
})
export class PythonPackageManagerService {
  private python: any;
  private micropip: any;
  private journal?: PyodideFileJournal;
  private cdnModuleService?: CdnModuleService;

  // Package tracking
  private readonly userInstalledPackages = new Set<string>();
  private readonly installationTrackers = new Map<string, PackageInstallationTracker>();

  // Package scanning configuration
  private enablePackageScanning: boolean = false;

  // Global wheel modules
  // private static readonly GLOBAL_WHEEL_MODULES: string[] = [
  //   'numpy', 'pandas', 'matplotlib', 'scipy', 'scikit-learn',
  //   'requests', 'beautifulsoup4', 'lxml', 'pillow', 'opencv-python'
  // ];

  constructor() { }

  /**
   * Initialize the package manager
   */
  async initialize(python: any, micropip: any, journal?: PyodideFileJournal, cdnModuleService?: CdnModuleService): Promise<void> {
    this.python = python;
    this.micropip = micropip;
    this.journal = journal;
    this.cdnModuleService = cdnModuleService;
  }

  /**
   * Install a single Python package
   */
  // async installPackage(
  //   packageName: string,
  //   config: PackageInstallConfig = {}
  // ): Promise<PackageInstallResult> {
  //   const startTime = performance.now();
  //   const tracker: PackageInstallationTracker = {
  //     packageName,
  //     startTime,
  //     status: 'installing',
  //     installedFiles: []
  //   };

  //   this.installationTrackers.set(packageName, tracker);

  //   if (config.enableLogging) {
  //     console.log(`📦 Installing package: ${packageName}`);
  //   }

  //   try {
  //     // Check if already installed
  //     const verification = await this.verifyPackageInstallation(packageName);
  //     if (verification.isInstalled) {
  //       if (config.enableLogging) {
  //         console.log(`📦 Package ${packageName} already installed`);
  //       }

  //       tracker.status = 'completed';
  //       tracker.endTime = performance.now();

  //       return {
  //         success: true,
  //         packageName,
  //         version: verification.version,
  //         installedFiles: [],
  //         dependencies: [],
  //         duration: tracker.endTime - startTime
  //       };
  //     }

  //     // Install using micropip
  //     await this.micropip.install(packageName);

  //     // Verify installation
  //     const postInstallVerification = await this.verifyPackageInstallation(packageName);
  //     if (!postInstallVerification.isInstalled) {
  //       throw new Error(`Package ${packageName} installation verification failed`);
  //     }

  //     // Track the installation
  //     this.userInstalledPackages.add(packageName);
  //     tracker.status = 'completed';
  //     tracker.endTime = performance.now();

  //     // Journal the package installation
  //     if (this.journal) {
  //       // Note: We'll track package files if we can detect them
  //       this.journal.logPackageInstallation(packageName, []);
  //     }

  //     const duration = tracker.endTime - startTime;

  //     if (config.enableLogging) {
  //       console.log(`✅ Package ${packageName} installed successfully in ${duration.toFixed(2)}ms`);
  //     }

  //     return {
  //       success: true,
  //       packageName,
  //       version: postInstallVerification.version,
  //       installedFiles: tracker.installedFiles,
  //       dependencies: [],
  //       duration
  //     };

  //   } catch (error: any) {
  //     tracker.status = 'failed';
  //     tracker.error = error.message;
  //     tracker.endTime = performance.now();

  //     const duration = tracker.endTime - startTime;

  //     if (config.enableLogging) {
  //       console.error(`❌ Failed to install package ${packageName}:`, error);
  //     }

  //     return {
  //       success: false,
  //       packageName,
  //       installedFiles: [],
  //       dependencies: [],
  //       duration,
  //       error: error.message
  //     };
  //   }
  // }

  /**
   * Install multiple packages
   */
  // async installPackages(
  //   packageNames: string[],
  //   config: PackageInstallConfig = {}
  // ): Promise<BatchInstallResult> {
  //   const startTime = performance.now();
  //   const results: PackageInstallResult[] = [];

  //   if (config.enableLogging) {
  //     console.log(`📦 Installing ${packageNames.length} packages:`, packageNames);
  //   }

  //   // Install packages sequentially to avoid conflicts
  //   for (const packageName of packageNames) {
  //     const result = await this.installPackage(packageName, config);
  //     results.push(result);
  //   }

  //   const endTime = performance.now();
  //   const totalDuration = endTime - startTime;
  //   const successful = results.filter(r => r.success);
  //   const failed = results.filter(r => !r.success);

  //   const batchResult: BatchInstallResult = {
  //     success: failed.length === 0,
  //     totalPackages: packageNames.length,
  //     successfulInstalls: successful.map(r => r.packageName),
  //     failedInstalls: failed.map(r => ({
  //       packageName: r.packageName,
  //       error: r.error || 'Unknown error'
  //     })),
  //     totalDuration,
  //     averageDuration: totalDuration / packageNames.length
  //   };

  //   if (config.enableLogging) {
  //     console.log(`📦 Batch installation completed:`, {
  //       successful: batchResult.successfulInstalls.length,
  //       failed: batchResult.failedInstalls.length,
  //       duration: totalDuration.toFixed(2) + 'ms'
  //     });
  //   }

  //   return batchResult;
  // }

  /**
   * Install a wheel package
   */
  // TODO: rethink functionality split between this and PythonFileSystemHelper.installWheelPackage
  async installPackageWheel(
    fileSystemHelper: PythonFileSystemHelper,
    wheelInfo: WheelPackageInfo,
    _config: PackageInstallConfig = {}
  ): Promise<PackageInstallResult> {
    const startTime = performance.now();

    try {
      // Install using micropip with URL
      if (wheelInfo.url) {
        await this.micropip.install(wheelInfo.url);
      } else if (wheelInfo.filePath) {
        fileSystemHelper.installWheelPackage(wheelInfo.filePath);
      }

      // Verify installation (non-blocking for wheel packages)
      const verification = await this.verifyPackageInstallation(wheelInfo.name);
      if (!verification.isInstalled) {
        // Don't block - micropip has already installed the package files to the filesystem
      }

      // Track the installation
      this.userInstalledPackages.add(wheelInfo.name);

      // Register package name in Python log filter so its loggers are not suppressed
      const normalizedName = wheelInfo.name.replace(/-/g, '_').toLowerCase();
      try {
        this.python.runPython(
          `_ehq_allowed_log_prefixes.add('${normalizedName}')`
        );
      } catch {
        // Silently ignore if logger setup hasn't run yet
      }

      // Journal the package installation with metadata
      if (this.journal) {
        this.journal.logPackageInstallation(wheelInfo.name, []);
        // Also log the wheel file itself with metadata
        this.journal.logFileCreation(wheelInfo.url.replace('file://', ''), 'package', {
          origin: 'user',
          fileType: 'whl',
          timestamp: Date.now(),
          persistent: true,
          purpose: 'module'
        });
      }

      const duration = performance.now() - startTime;

      return {
        success: true,
        packageName: wheelInfo.name,
        version: wheelInfo.version || verification.version,
        installedFiles: [],
        dependencies: wheelInfo.dependencies || [],
        duration
      };

    } catch (error: any) {
      const duration = performance.now() - startTime;

      return {
        success: false,
        packageName: wheelInfo.name,
        installedFiles: [],
        dependencies: [],
        duration,
        error: error.message || error,
      };
    }
  }

  /**
   * Verify if a package is properly installed
   */
  async verifyPackageInstallation(packageName: string): Promise<PackageVerificationResult> {
    try {
      const result = this.python.runPython(`
        import sys
        import importlib
        import json

        result = {
          'is_installed': False,
          'can_import': False,
          'version': None,
          'location': None,
          'error': None
        }

        try:
          # Try to import the package
          module = importlib.import_module('${packageName}')
          result['can_import'] = True
          result['is_installed'] = True

          # Try to get version
          if hasattr(module, '__version__'):
            result['version'] = module.__version__
          elif hasattr(module, 'VERSION'):
            result['version'] = str(module.VERSION)

          # Try to get location
          if hasattr(module, '__file__'):
            result['location'] = module.__file__

        except ImportError as e:
          result['error'] = f"ImportError: {str(e)}"
        except Exception as e:
          result['error'] = f"Error: {str(e)}"

        json.dumps(result)
      `);

      return JSON.parse(result);

    } catch (error: any) {
      return {
        isInstalled: false,
        canImport: false,
        error: error.message
      };
    }
  }

  /**
   * Process global wheel modules
   */
  // async processGlobalWheelModules(config: PackageInstallConfig = {}): Promise<void> {
  //   if (!this.cdnModuleService) {
  //     console.warn('CDN Module Service not available for global wheel modules');
  //     return;
  //   }

  //   if (config.enableLogging) {
  //     console.log('🐍 Processing global wheel modules...');
  //   }

  //   for (const moduleName of PythonPackageManagerService.GLOBAL_WHEEL_MODULES) {
  //     try {
  //       const moduleInfo = await this.getGlobalWheelModuleInfo(moduleName);
  //       if (moduleInfo && !moduleInfo.isInstalled) {
  //         await this.installGlobalWheelModule(moduleInfo, config);
  //       }
  //     } catch (error) {
  //       if (config.enableLogging) {
  //         console.warn(`Warning: Could not process global module ${moduleName}:`, error);
  //       }
  //     }
  //   }

  //   if (config.enableLogging) {
  //     console.log('✅ Global wheel modules processing completed');
  //   }
  // }

  /**
   * Get information about a global wheel module
   */
  // private async getGlobalWheelModuleInfo(moduleName: string): Promise<GlobalWheelModule | null> {
  //   if (!this.cdnModuleService) {
  //     return null;
  //   }

  //   try {
  //     const packages = await firstValueFrom(this.cdnModuleService.getFromPyPi(moduleName));

  //     if (!packages.files || packages.files.length === 0) {
  //       return null;
  //     }

  //     const wheelFiles = packages.files
  //       .filter((file: any) => file.url.endsWith('.whl'))
  //       .map((file: any) => ({
  //         name: moduleName,
  //         url: file.url,
  //         version: this.extractVersionFromUrl(file.url),
  //         size: file.size
  //       }));

  //     if (wheelFiles.length === 0) {
  //       return null;
  //     }

  //     const isInstalled = await this.verifyPackageInstallation(moduleName);

  //     return {
  //       name: moduleName,
  //       files: wheelFiles,
  //       isInstalled: isInstalled.isInstalled
  //     };

  //   } catch (error) {
  //     console.warn(`Could not get info for global module ${moduleName}:`, error);
  //     return null;
  //   }
  // }

  /**
   * Install a global wheel module
   */
  // private async installGlobalWheelModule(
  //   moduleInfo: GlobalWheelModule,
  //   config: PackageInstallConfig
  // ): Promise<void> {
  //   if (moduleInfo.files.length === 0) {
  //     return;
  //   }

  //   // Use the latest wheel file
  //   const wheelFile = moduleInfo.files[moduleInfo.files.length - 1];

  //   if (config.enableLogging) {
  //     console.log(`📦 Installing global wheel module: ${moduleInfo.name}`);
  //   }

  //   await this.installPackageWheel(wheelFile, config);
  // }

  /**
   * Extract version from wheel URL
   */
  // private extractVersionFromUrl(url: string): string | undefined {
  //   const versionMatch = url.match(/[/-](\d+(?:\.\d+)*(?:\.\w+)*)[/-]/);
  //   return versionMatch ? versionMatch[1] : undefined;
  // }

  /**
   * Get list of user-installed packages
   */
  getUserInstalledPackages(): string[] {
    return Array.from(this.userInstalledPackages);
  }

  /**
   * Get package installation tracker
   */
  getInstallationTracker(packageName: string): PackageInstallationTracker | undefined {
    return this.installationTrackers.get(packageName);
  }

  /**
   * Get all installation trackers
   */
  getAllInstallationTrackers(): Map<string, PackageInstallationTracker> {
    return new Map(this.installationTrackers);
  }

  /**
   * Clear installation tracking data
   */
  clearTrackingData(): void {
    this.installationTrackers.clear();
  }

  /**
   * Reset user installed packages list
   */
  resetUserInstalledPackages(): void {
    this.userInstalledPackages.clear();
  }

  /**
   * Get installation statistics
   */
  getInstallationStats(): {
    totalInstalled: number;
    successfulInstalls: number;
    failedInstalls: number;
    averageInstallTime: number;
  } {
    const trackers = Array.from(this.installationTrackers.values());
    const completed = trackers.filter(t => t.endTime);
    const successful = completed.filter(t => t.status === 'completed');
    const failed = completed.filter(t => t.status === 'failed');

    const totalTime = completed.reduce((sum, t) => {
      return sum + (t.endTime! - t.startTime);
    }, 0);

    return {
      totalInstalled: this.userInstalledPackages.size,
      successfulInstalls: successful.length,
      failedInstalls: failed.length,
      averageInstallTime: completed.length > 0 ? totalTime / completed.length : 0
    };
  }

  /**
   * Configure package scanning behavior
   * @param enable - true to enable full directory scanning (may capture system files)
   *                 false to rely on FS proxy journaling only (recommended)
   */
  // setPackageScanning(enable: boolean): void {
  //   this.enablePackageScanning = enable;
  //   console.log(`📦 Package scanning ${enable ? 'enabled' : 'disabled'} - ${enable
  //     ? 'WARNING: May journal system files!'
  //     : 'Using FS proxy journaling only (recommended)'
  //     }`);
  // }

  /**
   * Extract package name from wheel filename
   */
  extractPackageName(wheelFileName: string): string {
    // Remove file extension
    let packageName = wheelFileName.replace(/\.(whl|tar\.gz)$/, '');

    // Extract name part before version (before first dash followed by digit)
    const match = packageName.match(/^([^-]+)(?:-\d)/);
    if (match) {
      packageName = match[1];
    }

    // Handle underscores and normalize
    return packageName.replace(/_/g, '-').toLowerCase();
  }

  /**
   * Verify package installation by attempting to import it
   */
  async verifyPackageViaImport(packageName: string): Promise<boolean> {
    if (!this.python) {
      return false;
    }

    try {
      // Transform package name to valid Python module name (e.g., 'my-package' → 'my_package')
      const moduleName = packageName.replace(/-/g, '_').toLowerCase();

      const result = this.python.runPython(`
import sys
try:
    # Clear module cache if already loaded to ensure fresh import
    if '${moduleName}' in sys.modules:
        del sys.modules['${moduleName}']

    # Try to import the module
    __import__('${moduleName}')
    True  # Success
except ImportError:
    False  # Failed to import
except Exception as e:
    # Other errors (syntax, etc.) - consider as failed
    False
      `);

      return Boolean(result);
    } catch (error) {
      console.warn('Python package manager: module import check failed', { packageName, error });
      return false;
    }
  }

  // REMOVED: trackPackageInstallation() - overly complex, not needed with simplified approach
  // Packages are tracked via journal during installation

  // REMOVED: installFromWheel() - duplicate of installPackageWheel()
  // Use installPackageWheel() instead

  /**
   * Get all files recursively from a directory
   */
  private getAllFilesRecursive(dirPath: string): string[] {
    const files: string[] = [];

    const explore = (path: string) => {
      try {
        const items = this.python.FS.readdir(path);
        for (const item of items) {
          if (item === '.' || item === '..') continue;

          const fullPath = `${path}/${item}`;
          try {
            const stats = this.python.FS.stat(fullPath);
            if (this.python.FS.isFile(stats.mode)) {
              files.push(fullPath);
            } else if (this.python.FS.isDir(stats.mode)) {
              explore(fullPath);
            }
          } catch (error) {
            console.debug('Python package manager: skip inaccessible file', { fullPath, error });
          }
        }
      } catch (error) {
        console.debug('Python package manager: skip inaccessible directory', { path, error });
      }
    };

    explore(dirPath);
    return files;
  }

  // REMOVED: Complex tracking helpers - not needed with simplified approach
  // Package installation is tracked via journal during micropip.install()
}
